import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list with a "Load More" button
 * instead of infinite scroll.
 */
export default class BlogPostsLoadMore extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    // Disconnect the infinite scroll observer since we're using a button
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = undefined;
    }

    // Set up click handler for load more button
    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', this.#handleLoadMore);
      this.#updateButtonVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.removeEventListener('click', this.#handleLoadMore);
    }
  }

  #handleLoadMore = async () => {
    const { loadMoreButton } = this.refs;
    if (!loadMoreButton) return;

    // Show loading state
    loadMoreButton.setAttribute('aria-busy', 'true');
    loadMoreButton.disabled = true;

    // Trigger the next page render from parent class
    await this.renderNextPage();

    // Reset button state
    loadMoreButton.removeAttribute('aria-busy');
    loadMoreButton.disabled = false;

    // Hide button if no more pages
    this.#updateButtonVisibility();
  };

  #updateButtonVisibility() {
    const { loadMoreButton, grid } = this.refs;
    if (!loadMoreButton || !grid) return;

    const lastPage = Number(grid.getAttribute('last-page'));
    const currentMaxPage = this.#getCurrentMaxPage();

    if (currentMaxPage >= lastPage) {
      loadMoreButton.style.display = 'none';
    } else {
      loadMoreButton.style.display = '';
    }
  }

  #getCurrentMaxPage() {
    const { cards } = this.refs;
    if (!cards || cards.length === 0) return 1;

    let maxPage = 1;
    for (const card of cards) {
      const page = Number(card.getAttribute('data-page'));
      if (page > maxPage) maxPage = page;
    }
    return maxPage;
  }

  /**
   * Expose the render method so we can call it from the button click
   */
  async renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.getNextPageInfo();
    if (!nextPage) return;

    // Wait for the page to be fetched if not already cached
    let attempts = 0;
    while (!this.pages.has(nextPage.page) && attempts < 50) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    const pageHTML = this.pages.get(nextPage.page);
    if (!pageHTML) return;

    // Parse and append the new items
    const parser = new DOMParser();
    const doc = parser.parseFromString(pageHTML, 'text/html');
    const newGrid = doc.querySelector('[ref="grid"]');
    if (!newGrid) return;

    const newCards = newGrid.querySelectorAll('[ref="cards[]"]');
    grid.append(...newCards);

    // Update refs
    this.refs.cards = [...this.querySelectorAll('[ref="cards[]"]')];

    // Update URL
    // history.pushState('', '', nextPage.url.toString());

    // Prefetch next page
    this.pages.delete(nextPage.page);
    this.prefetchNextPage();
  }

  getNextPageInfo() {
    const currentMaxPage = this.#getCurrentMaxPage();
    const nextPageNum = currentMaxPage + 1;
    const lastPage = Number(this.refs.grid?.getAttribute('last-page'));

    if (nextPageNum > lastPage) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPageNum));

    return { page: nextPageNum, url };
  }

  prefetchNextPage() {
    const nextPage = this.getNextPageInfo();
    if (nextPage) {
      import('@theme/section-renderer').then(({ sectionRenderer }) => {
        sectionRenderer.getSectionHTML(this.sectionId, true, nextPage.url).then((html) => {
          this.pages.set(nextPage.page, html);
        });
      });
    }
  }
}

if (!customElements.get('blog-posts-load-more')) {
  customElements.define('blog-posts-load-more', BlogPostsLoadMore);
}
